import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  type Unsubscribe,
} from 'firebase/firestore'
import { db } from '@/services/firebase/config'
import type { Bid, AnonymizedBid } from '@/types/bid'

const LISTINGS_COLLECTION = 'listings'
const BIDS_COLLECTION = 'bids'

// Calculate minimum bid increment based on current price.
// TradeMe scaling ladder (2026-07-02, client request — bug qqIobDfI). The client
// reversed the earlier flat $1 ladder (2026-07-01) and asked us to mirror
// TradeMe's graduated minimum-bid increments exactly. Bands are inclusive of
// their lower bound (e.g. a $5.00 bid uses the $0.50 step). MUST stay in sync
// with the authoritative copy in functions/src/auction/bid-resolver.ts.
export function getMinimumBidIncrement(currentBid: number): number {
  if (currentBid < 5) return 0.2
  if (currentBid < 10) return 0.5
  if (currentBid < 50) return 1
  if (currentBid < 100) return 2
  if (currentBid < 500) return 5
  if (currentBid < 1000) return 10
  if (currentBid < 5000) return 20
  return 50
}

// Get minimum next bid amount
export function getMinimumNextBid(currentBid: number): number {
  return currentBid + getMinimumBidIncrement(currentBid)
}

// Get bid history for a listing
export async function getBidHistory(listingId: string): Promise<Bid[]> {
  const q = query(
    collection(db, LISTINGS_COLLECTION, listingId, BIDS_COLLECTION),
    orderBy('createdAt', 'desc'),
    limit(50)
  )

  const snapshot = await getDocs(q)
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    listingId,
    ...doc.data(),
  })) as Bid[]
}

// Subscribe to bid history (real-time)
export function subscribeToBidHistory(
  listingId: string,
  callback: (bids: Bid[]) => void
): Unsubscribe {
  const q = query(
    collection(db, LISTINGS_COLLECTION, listingId, BIDS_COLLECTION),
    orderBy('createdAt', 'desc'),
    limit(50)
  )

  return onSnapshot(q, (snapshot) => {
    const bids = snapshot.docs.map((doc) => ({
      id: doc.id,
      listingId,
      ...doc.data(),
    })) as Bid[]
    callback(bids)
  })
}

// Build display names for bid history. Prefers the bidder's real display name
// (stored on each bid) so buyers see "Jane Smith" rather than "Bidder 1". Falls
// back to the anonymized "Bidder N" pattern when a bid has no stored name or
// it's the legacy "Anonymous" placeholder. Current user always shows as "You".
export function anonymizeBidHistory(bids: Bid[], currentUserId?: string): AnonymizedBid[] {
  const bidderMap = new Map<string, string>()
  let bidderCount = 0

  const sortedBids = [...bids].sort(
    (a, b) => (a.createdAt?.toMillis?.() || 0) - (b.createdAt?.toMillis?.() || 0)
  )

  sortedBids.forEach((bid) => {
    if (!bidderMap.has(bid.bidderId)) {
      bidderCount++
      bidderMap.set(bid.bidderId, `Bidder ${bidderCount}`)
    }
  })

  return bids.map((bid) => {
    if (bid.bidderId === currentUserId) {
      return { ...bid, displayName: 'You' }
    }
    const realName = (bid.bidderName || '').trim()
    const usableName = realName && realName.toLowerCase() !== 'anonymous' ? realName : null
    return {
      ...bid,
      displayName: usableName || bidderMap.get(bid.bidderId) || 'Bidder',
    }
  })
}

// Place a bid (via Cloud Function for security)
export async function placeBid(
  listingId: string,
  _bidderId: string,
  _bidderName: string,
  amount: number
): Promise<{ success: boolean; message: string; newBid?: number }> {
  try {
    const { getFunctions, httpsCallable } = await import('firebase/functions')
    const fns = getFunctions(undefined, 'australia-southeast1')
    const fn = httpsCallable<
      { listingId: string; amount: number },
      { success: boolean; message: string; newBid?: number }
    >(fns, 'auctionPlaceBid')
    const result = await fn({ listingId, amount })
    return result.data
  } catch (error: any) {
    const message = error?.message || 'Failed to place bid'
    return { success: false, message }
  }
}

// Set auto-bid / proxy bid (via Cloud Function for security)
export async function setAutoBid(
  listingId: string,
  _bidderId: string,
  _bidderName: string,
  maxAmount: number
): Promise<{ success: boolean; message: string }> {
  try {
    const { getFunctions, httpsCallable } = await import('firebase/functions')
    const fns = getFunctions(undefined, 'australia-southeast1')
    const fn = httpsCallable<
      { listingId: string; maxAmount: number },
      { success: boolean; message: string }
    >(fns, 'auctionSetAutoBid')
    const result = await fn({ listingId, maxAmount })
    return result.data
  } catch (error: any) {
    const message = error?.message || 'Failed to set auto-bid'
    return { success: false, message }
  }
}

// End an auction early (seller/admin). Runs full settlement server-side:
// determines the winner from bids, creates the order, notifies everyone.
// Throws on failure so the caller can surface the HttpsError message.
export async function endAuctionNow(
  listingId: string
): Promise<{ status: 'sold' | 'closed' }> {
  const { getFunctions, httpsCallable } = await import('firebase/functions')
  const fns = getFunctions(undefined, 'australia-southeast1')
  const fn = httpsCallable<{ listingId: string }, { status: 'sold' | 'closed' }>(
    fns,
    'auctionEndNow'
  )
  const result = await fn({ listingId })
  return result.data
}

// Relist an ended auction as a fresh one with a new end date (clears old bids
// + ended/settled flags server-side). Throws on failure.
export async function relistAuction(
  listingId: string,
  durationDays = 7
): Promise<{ status: string; expiresAt: number; durationDays: number }> {
  const { getFunctions, httpsCallable } = await import('firebase/functions')
  const fns = getFunctions(undefined, 'australia-southeast1')
  const fn = httpsCallable<
    { listingId: string; durationDays: number },
    { status: string; expiresAt: number; durationDays: number }
  >(fns, 'auctionRelist')
  const result = await fn({ listingId, durationDays })
  return result.data
}

// Buy out a zero-bid auction at its buy-now price (via Cloud Function). Ends the
// auction and creates a pending winner order for the buyer. Throws on failure so
// the caller can surface the HttpsError message.
export async function buyOutAuction(
  listingId: string
): Promise<{ orderId: string; paymentMethod: string; amount: number }> {
  const { getFunctions, httpsCallable } = await import('firebase/functions')
  const fns = getFunctions(undefined, 'australia-southeast1')
  const fn = httpsCallable<{ listingId: string }, { orderId: string; paymentMethod: string; amount: number }>(
    fns,
    'auctionBuyNow'
  )
  const result = await fn({ listingId })
  return result.data
}

// Get user's bids on a listing
export async function getUserBidsOnListing(
  listingId: string,
  userId: string
): Promise<Bid[]> {
  const q = query(
    collection(db, LISTINGS_COLLECTION, listingId, BIDS_COLLECTION),
    where('bidderId', '==', userId),
    orderBy('createdAt', 'desc')
  )

  const snapshot = await getDocs(q)
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    listingId,
    ...doc.data(),
  })) as Bid[]
}
